import { useMemo } from "react";

import { Combobox, type ComboboxListItem } from "@prequel/shared-components";

export type ImportColumnOption = {
  name: string;
  prequelType?: string;
  rawType?: string;
};

export type ColumnTag = {
  text: string;
  className: string;
};

type ColumnComboboxProps = {
  label: string;
  columns: ImportColumnOption[];
  selectedColumn: string;
  setSelectedColumn: (column: string) => void;
  tags?: ColumnTag[];
  helperText?: string;
  required?: boolean;
  optional?: boolean;
  disabled?: boolean;
  allowCustomValue?: boolean;
  placeholder?: string;
};

const getColumnTypeLabel = (column: ImportColumnOption) => column.prequelType || column.rawType;

const ColumnCombobox = ({
  label,
  columns,
  selectedColumn,
  setSelectedColumn,
  tags = [],
  helperText,
  required = false,
  optional = false,
  disabled = false,
  allowCustomValue = true,
  placeholder = "Select an option",
}: ColumnComboboxProps) => {
  const items = useMemo<ComboboxListItem[]>(
    () =>
      columns.map((column) => ({
        key: column.name,
        searchValue: `${column.name} ${column.prequelType ?? ""} ${column.rawType ?? ""}`,
        text: (
          <>
            <span>{column.name}</span>
            {getColumnTypeLabel(column) && (
              <span className="ml-2 text-xs text-gray-500">{getColumnTypeLabel(column)}</span>
            )}
          </>
        ),
      })),
    [columns],
  );

  return (
    <div>
      <Combobox
        label={label}
        labelAccessory={tags.map((tag) => (
          <span
            key={tag.text}
            className={`
              rounded-full px-2 py-0.5 text-xs font-medium
              ${tag.className}
            `}
          >
            {tag.text}
          </span>
        ))}
        value={selectedColumn}
        onChange={setSelectedColumn}
        items={items}
        allowCustomValue={allowCustomValue}
        placeholder={placeholder}
        subtext={helperText}
        disabled={disabled}
        clearLabel={optional ? `Clear ${label}` : "Clear selection"}
        customValueLabel={(value) => value}
      />
      {required && !selectedColumn && <input tabIndex={-1} className="sr-only" required value="" onChange={() => {}} />}
    </div>
  );
};

export default ColumnCombobox;
